// announcements.js — Phase 115 promo pop-up helpers shared by the public
// coach website, the parent portal and the Website Builder editor.
// ---------------------------------------------------------------------------
// Dismissal is PER SESSION (sessionStorage) and keyed by surface + coach +
// the server-managed `version`. So a parent who closes the pop-up won't see
// it again in that session — until they log back in (we clear the keys on
// login/logout) or the coach changes the pop-up (version bumps).

const PREFIX = 'sp_ann_';

const storage = () => {
    try { return window.sessionStorage; } catch { return null; }
};

export function announcementKey(scope, coachKey) {
    return `${PREFIX}${scope}_${coachKey || 'coach'}`;
}

export function isAnnouncementDismissed(scope, coachKey, version) {
    const s = storage();
    if (!s) return false;
    try {
        return s.getItem(announcementKey(scope, coachKey)) === String(version ?? 1);
    } catch { return false; }
}

export function dismissAnnouncement(scope, coachKey, version) {
    const s = storage();
    if (!s) return;
    try { s.setItem(announcementKey(scope, coachKey), String(version ?? 1)); } catch { /* ignore */ }
}

/** Forget every dismissal — called on login/logout so re-login re-shows. */
export function clearAnnouncementDismissals() {
    const s = storage();
    if (!s) return;
    try {
        const keys = [];
        for (let i = 0; i < s.length; i += 1) {
            const k = s.key(i);
            if (k && k.startsWith(PREFIX)) keys.push(k);
        }
        keys.forEach((k) => s.removeItem(k));
    } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Action button
// ---------------------------------------------------------------------------
export const ANNOUNCEMENT_ACTIONS = [
    { id: 'none', label: 'No button', defaultLabel: '' },
    { id: 'register', label: 'Register / Enroll', defaultLabel: 'Register' },
    { id: 'call', label: 'Call Now', defaultLabel: 'Call Now' },
    { id: 'programs', label: 'View Programs', defaultLabel: 'View Programs' },
    { id: 'link', label: 'Custom link', defaultLabel: 'Learn more' },
];

export function announcementButtonLabel(ann) {
    if (!ann) return '';
    const type = ann.action_type || (ann.button_url ? 'link' : 'none');
    if (type === 'none') return '';
    if ((ann.button_label || '').trim()) return ann.button_label.trim();
    return ANNOUNCEMENT_ACTIONS.find((a) => a.id === type)?.defaultLabel || 'Learn more';
}

/**
 * Resolve what the action button should do for a given surface.
 *   ctx = { surface: 'website' | 'portal', slug, navigate, onRegister,
 *           onPrograms }
 * Returns null (no button) or { label, href?, external?, onClick? }.
 */
export function resolveAnnouncementAction(ann, ctx = {}) {
    if (!ann) return null;
    const type = ann.action_type || (ann.button_url ? 'link' : 'none');
    const label = announcementButtonLabel(ann);
    if (type === 'none' || !label) return null;
    const slug = ann.slug || ctx.slug || '';
    switch (type) {
        case 'call': {
            const digits = (ann.phone || '').replace(/[^\d+]/g, '');
            if (!digits) return null;
            return { label, href: `tel:${digits}` };
        }
        case 'register': {
            const pkg = ann.package_id || '';
            if (ctx.surface === 'portal') {
                return { label, onClick: () => ctx.navigate?.(`/parent/coach/${slug}/book${pkg ? `?pkg=${encodeURIComponent(pkg)}` : ''}`) };
            }
            if (ctx.onRegister) return { label, onClick: () => ctx.onRegister(pkg) };
            return { label, onClick: () => ctx.navigate?.(`/coach/${slug}/register${pkg ? `?pkg=${encodeURIComponent(pkg)}` : ''}`) };
        }
        case 'programs': {
            if (ctx.surface === 'portal') {
                return { label, onClick: () => ctx.navigate?.(`/parent/coach/${slug}/book`) };
            }
            if (ctx.onPrograms) return { label, onClick: ctx.onPrograms };
            return { label, onClick: () => ctx.navigate?.(`/coach/${slug}/programs`) };
        }
        case 'link':
        default: {
            const url = (ann.button_url || '').trim();
            if (!url) return null;
            if (/^https?:\/\//i.test(url)) {
                try {
                    const u = new URL(url);
                    if (u.origin === window.location.origin) {
                        return { label, onClick: () => ctx.navigate?.(u.pathname + u.search + u.hash) };
                    }
                } catch { /* fall through */ }
                return { label, href: url, external: true };
            }
            if (url.startsWith('/')) return { label, onClick: () => ctx.navigate?.(url) };
            return { label, href: url };
        }
    }
}
